import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import { useAuth } from 'firebaseHooks/AuthContext';
import recordAuthor from 'utils/recordAuthor';
import {
  collection, doc, serverTimestamp, setDoc,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import useGlobal from 'global-state/store';
import { getColumnMappings, getMappingConfig } from '../../admin/inventories/mappingUtils';
import { clearMeasurementsRecord, fillingMeasurementsToResolve } from '../utils/fillingMeasurements';
import { InventoryQueueRegistryContext } from './useInventoryQueueRegistry';
import {
  calculateProgressPercentageABV, calculateProgressPercentageVolume,
  determineNewInventoryStatus, getMappedContainerCompletion,
} from '../utils/operationUtils';

export default function useChangeFillingStatus({
  inventoryData,
  itemData,
  setInventoryData,
}) {
  const firestore = useFirestore();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const [loading, setLoading] = useState(false);
  const queueRegistry = useContext(InventoryQueueRegistryContext);
  const mappingConfig = getMappingConfig(inventoryData);
  const columnMappings = getColumnMappings(inventoryData);
  const fillingCsvName = mappingConfig.columns.filling.mapping.csvName;
  const currentRawStatus = itemData[fillingCsvName];

  const getContainerId = (item) => {
    const ivtContainerIdMapping = mappingConfig.columns.ivtContainerId.mapping.csvName;
    if (Array.isArray(ivtContainerIdMapping)) {
      return ivtContainerIdMapping.map((colName) => item[colName]).join(' / ');
    }
    return item[ivtContainerIdMapping];
  };

  const measurementsToResolve = (newStatus, formValues) => fillingMeasurementsToResolve({
    itemData, columnMappings, newStatus, formValues,
  });

  // Resolves true once the change is committed locally (offline queue / Firestore cache),
  // false when nothing was saved: callers must then keep the operator's entries.
  const changeFillingStatus = async (newStatus, { clearKeys = [] } = {}) => {
    setLoading(true);

    const organizationId = globalState.activeOrganization;
    if (!organizationId || !currentUser?.uid) {
      globalActions.setSnackbarMessage({ message: t('unexpected_error'), severity: 'error' });
      setLoading(false);
      return false;
    }

    const containerId = getContainerId(itemData);
    const clearRecord = clearMeasurementsRecord({
      itemData, columnMappings, containerId, newStatus, keys: clearKeys,
    });
    if (clearRecord) {
      try {
        // Local commit of the offline queue; the server acknowledgement is not awaited.
        await queueRegistry.enqueueRecord(inventoryData.id, clearRecord);
      } catch (error) {
        globalActions.setSnackbarMessage({
          message: `${t('unexpected_error')} : ${error.message}`, severity: 'warning',
        });
        setLoading(false);
        return false;
      }
    }
    const cleared = clearRecord ? Object.fromEntries(Object.keys(clearRecord.formValues)
      .map((key) => [columnMappings[key].csvName, ''])) : {};
    const updatedContainersArray = inventoryData.containersArray.map((container) => {
      if (getContainerId(container) === containerId) {
        const updated = {
          ...container,
          ...cleared,
          [fillingCsvName]: newStatus,
        };
        return { ...updated, ...getMappedContainerCompletion(updated, mappingConfig) };
      }
      return container;
    });

    const progressABVPercentage = calculateProgressPercentageABV(updatedContainersArray);
    const progressVolumePercentage = calculateProgressPercentageVolume(updatedContainersArray);
    const updates = {
      containersArray: updatedContainersArray,
      progressABVPercentage,
      progressVolumePercentage,
      status: determineNewInventoryStatus(inventoryData.status, progressABVPercentage, progressVolumePercentage),
    };

    globalActions.updateLocalInventory(inventoryData.id, updates);
    setInventoryData({ ...inventoryData, ...updates });

    const inventoryRef = doc(
      firestore,
      'organizations',
      organizationId,
      'apps',
      'inventory',
      'inventories',
      inventoryData.id,
    );
    const fillingStatusRecordsRef = collection(firestore, `${inventoryRef.path}/fillingStatusRecords`);
    const fillingStatusRecordRef = doc(fillingStatusRecordsRef);

    setDoc(fillingStatusRecordRef, {
      ...recordAuthor(currentUser),
      containerId,
      fillingStatus: newStatus,
      oldFillingStatus: currentRawStatus,
      // Keep the operator's action time even if Firestore syncs much later.
      occurredAt: new Date(),
      timeStamp: serverTimestamp(),
    }).catch((error) => {
      try {
        Sentry.captureException(error, {
          tags: { area: 'fillingStatus', action: 'writeRecord' },
          extra: {
            organizationId,
            inventoryId: inventoryData.id,
            recordId: fillingStatusRecordRef.id,
            containerId,
          },
        });
      } catch (_) { /* no-op */ }

      try {
        globalActions.setSnackbarMessage({
          message: `${t('unexpected_error')} : ${error.message}`,
          severity: 'warning',
        });
      } catch (_) { /* no-op */ }
    });

    globalActions.setSnackbarMessage({ message: t('success'), severity: 'success', duration: 2000 });
    setLoading(false);
    return true;
  };

  return {
    changeFillingStatus,
    measurementsToResolve,
    currentRawStatus,
    loading,
  };
}
